// src/utils/floodThresholds.js
// 洪水深度阈值标准配置文件
// 基于 NWS (National Weather Service) 标准 + 车辆通行能力

/**
 * 洪水深度阈值标准
 * 参考：
 * - NWS Flood Categories: https://www.nssl.noaa.gov/education/svrwx101/floods/faq/
 * - Vehicle Safety Standards
 * - NYC FloodNet 实际部署经验
 */
export const FLOOD_THRESHOLDS = {
  minor: {
    min: 0,
    max: 6,
    color: '#eab308', // yellow-500
    bgColor: '#fef3c7', // yellow-100
    borderColor: '#f59e0b', // yellow-500
    label: 'Minor Flooding',
    shortLabel: 'Minor',
    description: 'Minimal impact, pedestrian accessible',
    vehicleImpact: 'Safe for most vehicles',
    nwsCategory: 'Minor Flooding',
    icon: '🟡',
    severity: 'minor'
  },
  moderate: {
    min: 6,
    max: 12,
    color: '#f97316', // orange-500
    bgColor: '#fed7aa', // orange-100
    borderColor: '#ea580c', // orange-600
    label: 'Moderate Flooding', 
    shortLabel: 'Moderate',
    description: 'Some road/structure inundation',
    vehicleImpact: 'Unsafe for regular cars',
    nwsCategory: 'Moderate Flooding',
    icon: '🟠',
    severity: 'moderate'
  },
  severe: {
    min: 12,
    max: Infinity,
    color: '#dc2626', // red-600
    bgColor: '#fecaca', // red-100
    borderColor: '#991b1b', // red-800
    label: 'Major Flooding',
    shortLabel: 'Major',
    description: 'Extensive inundation, evacuations needed', 
    vehicleImpact: 'Dangerous for all vehicles',
    nwsCategory: 'Major Flooding',
    icon: '🔴',
    severity: 'severe'
  }
};

/**
 * 根据水深获取严重程度
 * @param {number} depthInches - 水深(英寸)
 * @returns {string} - 严重程度 (minor, moderate, severe)
 */
export const getSeverityFromDepth = (depthInches) => {
  if (!depthInches || depthInches < 0) return 'minor';
  if (depthInches < FLOOD_THRESHOLDS.moderate.min) return 'minor';
  if (depthInches < FLOOD_THRESHOLDS.severe.min) return 'moderate';
  return 'severe';
};

/**
 * 获取阈值配置
 * @param {string} severity - 严重程度 (minor, moderate, severe)
 * @returns {Object} - 阈值配置对象
 */
export const getThresholdConfig = (severity) => {
  // 标准化 severity 输入
  const normalizedSeverity = severity?.toLowerCase();
  
  // 处理历史命名兼容性
  const severityMapping = {
    'mild': 'minor',
    'normal': 'minor',
    'minor': 'minor',
    'moderate': 'moderate',
    'severe': 'severe',
    'major': 'severe'
  };
  
  const mappedSeverity = severityMapping[normalizedSeverity] || 'minor';
  return FLOOD_THRESHOLDS[mappedSeverity] || FLOOD_THRESHOLDS.minor;
};

/**
 * 获取所有阈值用于图例显示
 * @returns {Array} - 阈值数组
 */
export const getAllThresholds = () => {
  return Object.entries(FLOOD_THRESHOLDS).map(([key, config]) => ({
    severity: key,
    ...config
  }));
};

/**
 * 格式化阈值范围显示
 * @param {Object} threshold - 阈值配置
 * @returns {string} - 格式化的范围文本
 */
export const formatThresholdRange = (threshold) => {
  if (threshold.max === Infinity) {
    return `${threshold.min}"+ `;
  }
  return `${threshold.min}"-${threshold.max}"`;
};

/**
 * 获取阈值分界线（用于可视化标记）
 * @returns {Array} - 分界线数组
 */
export const getThresholdBreakpoints = () => {
  return [
    {
      depth: FLOOD_THRESHOLDS.moderate.min, // 6"
      label: 'Minor → Moderate',
      color: '#f97316',
      isThreshold: true
    },
    {
      depth: FLOOD_THRESHOLDS.severe.min, // 12"
      label: 'Moderate → Major', 
      color: '#dc2626',
      isThreshold: true
    }
  ];
};

/**
 * 检查是否为阈值分界点
 * @param {number} depth - 深度值
 * @returns {boolean} - 是否为分界点
 */
export const isThresholdBreakpoint = (depth) => {
  return depth === 6 || depth === 12;
};

/**
 * 获取Badge组件的变体名称（向后兼容）
 * @param {string} severity - 严重程度
 * @returns {string} - Badge变体名称
 */
export const getBadgeVariant = (severity) => {
  const config = getThresholdConfig(severity);
  switch (config.severity) {
    case 'minor': return 'flood-mild';
    case 'moderate': return 'flood-moderate'; 
    case 'severe': return 'flood-severe';
    default: return 'flood-mild';
  }
};

/**
 * 阈值标准的元信息
 */
export const THRESHOLD_METADATA = {
  standard: 'NWS + Vehicle Safety',
  reference: 'https://www.nssl.noaa.gov/education/svrwx101/floods/faq/',
  description: 'Based on National Weather Service flood categories and vehicle safety standards',
  lastUpdated: '2025-01-29',
  notes: [
    'Minor (0-6"): Safe for most vehicles, minimal property impact',
    'Moderate (6-12"): Vehicle access limited, some structural inundation', 
    'Major (12"+): Dangerous conditions, extensive evacuations needed'
  ]
};

// 默认导出
export default {
  FLOOD_THRESHOLDS,
  getSeverityFromDepth,
  getThresholdConfig,
  getAllThresholds,
  formatThresholdRange,
  getThresholdBreakpoints,
  isThresholdBreakpoint,
  getBadgeVariant,
  THRESHOLD_METADATA
};